import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import MDButtonLoading from "components/MDLoadingButton";
import NegativeImage from "assets/images/NegativeSelfReporting.svg?react";
import PositiveImage from "assets/images/PossitiveSelfReporting.svg?react";
import { styles } from "./SelfReportScreen.styles";
import { ResultActionType } from "types/resultActions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getPendingUpload,
  getResult,
  setPendingUploadPath,
} from "store/slices/imageRecognition";
import {
  getOrStartEagerUpload,
  updateMetadataJson,
  uploadTestImages,
} from "services/imageUpload";
import {
  getPersistedImageDownloadUrls,
  getPersistedUploadPath,
  persistUploadPath,
} from "services/sessionPersistence";
import type { ResultStatus } from "types/imageRecognition";
import { SelfReportedResult } from "types/models";
import { logSelfReportAnalytics } from "services/selfReportLogger";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { getModelMetaDataTags } from "services/models";
import { formatImageUrlsForSentry } from "utils/sentryHelpers";
import useTestParams from "hooks/useTestParams.hook";

interface StepProps { 
  handleUpdateStep: (step: number) => void;
  setResultAction: (action: ResultActionType) => void;
}

const getEagerUploadKey = (sessionId: string | null): string =>
  sessionId ?? "no-session";

const SelfReportScreen: FC<StepProps> = ({ handleUpdateStep, setResultAction }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const pendingUpload = useAppSelector(getPendingUpload);
  const classificationResult = useAppSelector(getResult);
  const { sessionId: urlSessionId } = useTestParams();
  
  const [selectedResult, setSelectedResult] = useState<SelfReportedResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageDownloadUrlsRef = useRef<Array<string> | undefined>(undefined);

  // Eager upload on reaching step 5 — images + initial metadata.json (no selfReportedResult)
  useEffect(() => {
    if (!pendingUpload || pendingUpload.uploadPath) {
      return;
    }

    const eagerKey = getEagerUploadKey(pendingUpload.sessionId);
    let cancelled = false;

    getOrStartEagerUpload(eagerKey, {
      images: pendingUpload.images,
      testResult: pendingUpload.modelResult,
      metadata: pendingUpload.metadata,
      testType: pendingUpload.testType,
      sessionId: pendingUpload.sessionId,
      maxQualityImageIndex: pendingUpload.maxQualityImageIndex,
    })
      .then((uploadResult) => {
        if (cancelled) {
          return;
        }
        if (uploadResult.uploadPath) {
          dispatch(setPendingUploadPath(uploadResult.uploadPath));
          const effectiveSessionId = pendingUpload.sessionId ?? urlSessionId;
          persistUploadPath(
            effectiveSessionId,
            uploadResult.uploadPath,
            uploadResult.imageDownloadUrls
          );
        }
        imageDownloadUrlsRef.current = uploadResult.imageDownloadUrls;
        sentryLogMessage({
          message: "Images uploaded to S3 before self-report",
          level: "info",
          tags: {
            component: "SelfReportScreen",
            category: "eager_s3_upload",
            event_type: SENTRY_EVENT_TYPES.IMAGE_UPLOADED_PRE_SELF_REPORT,
            model_result: pendingUpload.modelResult,
            ...getModelMetaDataTags(),
          },
          extra: {
            uploadPath: uploadResult.uploadPath,
            timestamp: new Date().toISOString(),
            ...formatImageUrlsForSentry(uploadResult.imageDownloadUrls),
          },
        });
      })
      .catch((error) => {
        console.error("SelfReportScreen: Eager upload failed:", error);
      });

    return () => {
      cancelled = true;
    };
  // Intentionally keyed on sessionId/uploadPath so remount with same pending upload reuses memoized promise
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpload?.sessionId, pendingUpload?.uploadPath, dispatch, urlSessionId]);

  const handleSubmitResult = async (selfReportedResult: SelfReportedResult) => {
    if (isSubmitting) {
      return;
    }

    const submissionTimestamp = Date.now();
    setIsSubmitting(true);

    let imageDownloadUrls: Array<string> | undefined = imageDownloadUrlsRef.current;
    const modelResult: ResultStatus =
      pendingUpload?.modelResult ?? classificationResult ?? "INVALID";
    const effectiveSessionId = pendingUpload?.sessionId ?? urlSessionId;

    if (pendingUpload) {
      try {
        const eagerKey = getEagerUploadKey(pendingUpload.sessionId);
        let uploadPath = pendingUpload.uploadPath;

        // Await in-flight eager upload (or reuse completed one) before metadata update
        try {
          const eagerResult = await getOrStartEagerUpload(eagerKey, {
            images: pendingUpload.images,
            testResult: pendingUpload.modelResult,
            metadata: pendingUpload.metadata,
            testType: pendingUpload.testType,
            sessionId: pendingUpload.sessionId,
            maxQualityImageIndex: pendingUpload.maxQualityImageIndex,
          });
          uploadPath = uploadPath ?? eagerResult.uploadPath;
          imageDownloadUrls = eagerResult.imageDownloadUrls ?? imageDownloadUrls;
          if (eagerResult.uploadPath) {
            if (!pendingUpload.uploadPath) {
              dispatch(setPendingUploadPath(eagerResult.uploadPath));
            }
            persistUploadPath(
              effectiveSessionId,
              eagerResult.uploadPath,
              eagerResult.imageDownloadUrls
            );
          }
        } catch (eagerError) {
          console.warn(
            "SelfReportScreen: Eager upload unavailable on submit, will fallback if needed:",
            eagerError
          );
        }

        if (uploadPath) {
          await updateMetadataJson(
            uploadPath,
            pendingUpload.modelResult,
            pendingUpload.metadata,
            pendingUpload.sessionId,
            selfReportedResult
          );
        } else {
          // Fallback: full upload with selfReportedResult when eager never succeeded
          const uploadResult = await uploadTestImages(
            pendingUpload.images,
            pendingUpload.modelResult,
            pendingUpload.metadata,
            pendingUpload.testType,
            pendingUpload.sessionId,
            selfReportedResult,
            pendingUpload.maxQualityImageIndex
          );
          imageDownloadUrls = uploadResult?.imageDownloadUrls;
          if (uploadResult?.uploadPath) {
            persistUploadPath(
              effectiveSessionId,
              uploadResult.uploadPath,
              uploadResult.imageDownloadUrls
            );
          }
        }

        try {
          await logSelfReportAnalytics({
            timestamp: submissionTimestamp,
            selfReportedResult,
            modelResult,
            imageDownloadUrls,
          });
        } catch (error) {
          console.error("SelfReportScreen: logSelfReportAnalytics failed:", error);
        }
      } catch (error) {
        console.error('Failed to upload test images / update metadata:', error);
        logSelfReportAnalytics({
          timestamp: submissionTimestamp,
          selfReportedResult,
          modelResult: pendingUpload.modelResult,
          imageDownloadUrls: ['error'],
        }).catch((analyticsError) => {
          console.error('Failed to log analytics:', analyticsError);
        });
      }
    } else {
      // After refresh, pendingUpload is gone but uploadPath / image URLs may be in localStorage
      const restoredUploadPath = getPersistedUploadPath(effectiveSessionId);
      imageDownloadUrls =
        getPersistedImageDownloadUrls(effectiveSessionId) ?? imageDownloadUrls;

      if (restoredUploadPath) {
        try {
          await updateMetadataJson(
            restoredUploadPath,
            modelResult,
            undefined,
            effectiveSessionId,
            selfReportedResult
          );

          try {
            await logSelfReportAnalytics({
              timestamp: submissionTimestamp,
              selfReportedResult,
              modelResult,
              imageDownloadUrls,
            });
          } catch (error) {
            console.error("SelfReportScreen: logSelfReportAnalytics failed:", error);
          }
        } catch (error) {
          console.error('Failed to update metadata with restored uploadPath:', error);
          logSelfReportAnalytics({
            timestamp: submissionTimestamp,
            selfReportedResult,
            modelResult,
            imageDownloadUrls: ['error'],
          }).catch((analyticsError) => {
            console.error('Failed to log analytics:', analyticsError);
          });
        }
      } else {
        console.warn("SelfReportScreen: No pending upload data found");
      }
    }

    // Moved to inside if (pendingUpload) try section, remove if not causing issues
    // try {
    //   await logSelfReportAnalytics({
    //     timestamp: submissionTimestamp,
    //     selfReportedResult,
    //     modelResult,
    //     imageDownloadUrls,
    //   });
    // } catch (error) {
    //   console.error("SelfReportScreen: logSelfReportAnalytics failed:", error);
    // }

    try {
      sentryLogMessage({
        message: "Test completed",
        level: "info",
        tags: {
          component: "SelfReportScreen",
          category: "test_completed",
          event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST,
          ...getModelMetaDataTags(),
        },
        extra: {
          timestamp: new Date().toISOString(),
        },
      });

      sentryLogMessage({
        message: "Result submitted by user",
        level: "info",
        tags: {
          component: "SelfReportScreen",
          category: "result_submitted",
          event_type: SENTRY_EVENT_TYPES.RESULT_SUBMITTED,
          ...getModelMetaDataTags(),
        },
        extra: {
          selfReportedResult,
          modelResult,
          timestamp: new Date().toISOString(),
        },
      });

    } catch (error) {
      console.error("SelfReportScreen: Sentry logging or flush failed:", error);
    }

    setResultAction("DONE");
    handleUpdateStep(6);
  };

  const handleCardClick = (result: SelfReportedResult) => {
    setSelectedResult(result);
  };

  const handleReportIssue = () => {
    setResultAction("SELF_REPORTING_ISSUE");
    handleUpdateStep(6);
  };

  return (
    <div
      className={classes.container}
      aria-busy={isSubmitting}
      data-test-id="self-report-screen"
    >
      {isSubmitting ? (
        <div
          className={classes.submittingOverlay}
          role="status"
          aria-live="polite"
          data-test-id="self-report-submitting-overlay"
        >
          <CircularProgress aria-hidden size={48} />
        </div>
      ) : null}
      <div className={classes.helperText}>
        {t("SelfReportScreen:helperText")}
      </div>
      
      <div className={classes.optionsContainer}>
        <button
          type="button"
          className={`${classes.optionCard} ${
            selectedResult === "negative" ? classes.optionCardSelected : ""
          }`}
          disabled={isSubmitting}
          onClick={() => handleCardClick("negative")}
          data-test-id="self-report-option-negative"
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:oneLine")}
          </div>
          <div className={classes.optionImage}>
            <NegativeImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:negative")}
          </div>
        </button>
        <button
          type="button"
          className={`${classes.optionCard} ${
            selectedResult === "positive" ? classes.optionCardSelected : ""
          }`}
          disabled={isSubmitting}
          onClick={() => handleCardClick("positive")}
          data-test-id="self-report-option-positive"
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:twoLines")}
          </div>
          <div className={classes.optionImage}>
            <PositiveImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:positive")}
          </div>
        </button>
      </div>

      <div className={classes.buttonsSection}>
        <button
          type="button"
          className={classes.link}
          disabled={isSubmitting}
          onClick={handleReportIssue}
          data-test-id="self-report-report-issue"
        >
          {t("SelfReportScreen:reportIssue")}
        </button>
        <MDButtonLoading
          style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
          color={"primary"}
          variant="contained"
          type="submit"
          loading={isSubmitting}
          disabled={selectedResult === null}
          data-test-id="self-report-submit-button"
          onClick={() => {
            if (selectedResult !== null) {
              handleSubmitResult(selectedResult);
            }
          }}
        >
          {t("SelfReportScreen:submitButton")}
        </MDButtonLoading>
      </div>
    </div>
  );
};

export default SelfReportScreen;

